<!-- Tab.vue -->
<script setup>
import { ref, computed, markRaw } from 'vue';

defineOptions({
	name: 'EGS_Tab'
});

const props = defineProps({
	disabled: { type: Boolean, default: false },
	initialTab: {
		type: Number,
		default: 0
	}
});

const emit = defineEmits(['tab-changed']);
const tabs = ref([]);
/** The tab asked for (initialTab, then clicks). Tabs register one at a time, so it can point past the ones registered so far. */
const requestedTab = ref(props.initialTab);
/** The tab shown: the requested one once it has registered, the first one until then. */
const activeTab = computed(() => (requestedTab.value < tabs.value.length ? requestedTab.value : 0));

const selectTab = (index) => {
	if (props.disabled || !tabs.value[index]) {
		return;
	}
	requestedTab.value = index;
	emit('tab-changed', index);
};

const addTab = (tab) => {
	if (!tab?.label || !tab?.component) {
		console.warn('EGS_Tab requires both a label and component.', tab);
		return;
	}
	tabs.value.push(
		markRaw({
			...tab
		})
	);
};

const currentTabComponent = computed(() => tabs.value[activeTab.value]?.component ?? null);

defineExpose({ addTab });
</script>

<template>
	<div class="min-w-0 max-w-full">
		<div v-if="tabs.length" class="flex w-fit max-w-full flex-wrap gap-1 rounded-lg border border-surface bg-[var(--egs-content-background)] p-1 shadow-sm" role="tablist" aria-label="Profile sections">
			<button
				v-for="(tab, index) in tabs"
				:key="tab.key ?? index"
				type="button"
				class="cursor-pointer rounded-md border px-2.5 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
				:class="{
					'border-primary-700 bg-primary-700 text-white dark:border-primary-400 dark:bg-primary-400 dark:text-primary-950 shadow-sm': activeTab === index,
					'border-transparent text-color-secondary hover:bg-[var(--egs-content-hover)] hover:text-color': activeTab !== index
				}"
				role="tab"
				:aria-selected="activeTab === index"
				:disabled="disabled"
				:tabindex="activeTab === index ? 0 : -1"
				@click="selectTab(index)"
			>
				{{ tab.label }}
			</button>
		</div>
		<div class="min-w-0 pt-3" role="tabpanel">
			<component :is="currentTabComponent" v-if="currentTabComponent" />
			<div v-else class="flex min-h-24 items-center justify-center text-sm text-color-secondary">No tab content is available.</div>
		</div>
	</div>
</template>
